import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CaseContents } from './components/case-tree/CaseContents';
import { CaseBlocks } from './components/blocks/CaseBlocks';
import styles from './PatientCases.module.scss';
import { useEventCallback } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import {
  DataBlockVariant,
  GDTMessageDirection,
  GDTMessageGroup,
  HL7MessageDirection,
  HL7MessageGroup,
  NumberSource,
  QueryFactory,
  SortDirection,
  useEnabledDataBlockTypes,
  VisitListDto,
} from '@lmt/shared-types';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useQueryClient } from '@tanstack/react-query';

import queryString from 'query-string';
import { localizeCaseTitle } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import {
  CaseItem,
  CaseItemType,
  SubCaseItemType,
  TransportInfo,
} from './types';
import {
  getItemPrefix,
  getTransportStatus,
} from './components/case-tree/common';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import {
  caseInformationDataBlockTypeName,
  visitInformationDataBlockTypeName,
} from '@lmt/unicorn-shared/app/components/measurements/common';
import { useTabId } from '@lmt/unicorn-shared/app/components/keep-alive/TabContext';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import {
  getCurrentUrl,
  getTabId,
  useTabs,
} from '@lmt/unicorn-shared/app/features/navigation/operations';

type CaseWrapperProps = {
  patientId: number;
  caseId: number;
  setCaseBeingDeleted: (value: boolean) => void;
  openCaseTabOrEmpty: (caseId?: number, replace?: boolean) => void;
};
export const CaseWrapper: React.FC<CaseWrapperProps> = React.memo(
  function CaseWrapper(props) {
    const navigate = useNavigate();
    const modals = useModal();
    const queryClient = useQueryClient();
    const i18next = useScopedTranslation('patients.cases');
    const claims = useClaims();
    const tabId = useTabId();
    const tabs = useTabs();
    const forms = useGlobalForms();
    const dispatch = useUniDispatch();
    const location = useLocation();

    const { patientId, caseId, setCaseBeingDeleted, openCaseTabOrEmpty } =
      props;

    const [searchedItem, setSearchedItem] = useState<string | null>(null);

    useEffect(() => {
      const parsedString = queryString.parse(location.search);
      // trying to find element of case (visit, measurement, etc.) in URL that should be focused
      // (i.e. '?item=visit_1_measurement_2')
      const selectedItem = parsedString.item?.toString();
      if (selectedItem) {
        setSearchedItem(selectedItem);
      }
    }, [location.search]);

    const casesQuery = QueryFactory.CaseQuery.useGetCasesQuery({
      patientId: props.patientId,
    });
    const dataBlockTypes = useEnabledDataBlockTypes();
    const { data: gdtSettings } = QueryFactory.GDTSettingsQuery.useGetQuery({
      enabled: claims.permissions.gdt,
    });
    const { data: hl7OutgoingSettings } =
      QueryFactory.HL7OutgoingSettingsQuery.useGetQuery({
        enabled: claims.permissions.hl7,
      });
    const { data: patient } = QueryFactory.PatientQuery.useGetPatientQuery(
      props.patientId,
    );
    const isCaseQueriesEnabled =
      !!props.caseId &&
      !casesQuery.isFetching &&
      casesQuery.data &&
      casesQuery.data.data &&
      casesQuery.data.data.findIndex((x) => x.id === props.caseId) > -1;

    const caseQuery = QueryFactory.CaseQuery.useGetCaseQuery(
      { id: props.caseId! },
      {
        enabled: !!props.caseId,
      },
    );
    const visitsQuery =
      QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery(
        {
          patientId: patientId,
          caseId: props.caseId!,
        },
        {
          enabled: isCaseQueriesEnabled,
        },
      );

    const dataBlocksQuery = QueryFactory.DataBlockQuery.useGetQuery(
      {
        caseId: props.caseId,
      },
      {
        enabled: isCaseQueriesEnabled,
      },
    );

    const changeCurrentItem = useEventCallback((itemId: string) => {
      const elementId = `caseItem${itemId}`;
      document.getElementById(elementId)?.scrollIntoView();
      navigate({ hash: elementId }, { replace: true });
    });
    const removeCase = useEventCallback(async (caseId: number) => {
      const caseToRemove = casesQuery.data?.data?.find(
        (item) => item.id === caseId,
      );
      const caseTitle = !!caseToRemove
        ? localizeCaseTitle(caseToRemove)
        : localizeCaseTitle({
            id: caseId,
            number: caseId.toString(),
            numberSource: NumberSource.Autogenerated,
            startDate: new Date(),
          });
      const isConfirmed = await modals.showConfirm({
        title: `${i18next.t('confirmation_delete_title')}  ${caseTitle}`,
        text: i18next.t('confirmation_delete_text'),
        okButtonText: i18next.t('confirmation_delete_button'),
        okButtonColor: ButtonColor.SuperDanger,
      });
      if (isConfirmed) {
        try {
          setCaseBeingDeleted(true);
          await QueryFactory.CaseClient.removeCase(caseId);
          await casesQuery.refetch();
          queryClient.invalidateQueries({
            queryKey: QueryFactory.FileQuery.searchFilesQueryKey(),
          });
          openCaseTabOrEmpty();
        } catch (e) {
          await modals.showError({ text: convertToErrorString(e) });
        } finally {
          setCaseBeingDeleted(false);
        }
      }
    });
    const reopenCase = useEventCallback(async (caseId: number) => {
      try {
        await QueryFactory.CaseClient.reopenCase(caseId);
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.DataBlockQuery.getQueryKey(),
        });
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      }
    });

    const { data: hl7TemplateRules } =
      QueryFactory.HL7TemplateRulesQuery.useGetQuery(
        {
          subtenantId: patient?.subtenantId,
          isActive: true,
          messageGroup: HL7MessageGroup.DataBlock,
        },
        {
          enabled: claims.permissions.hl7 && !!patient?.subtenantId,
        },
      );
    const { data: gdtTemplateRules } =
      QueryFactory.GDTTemplateRulesQuery.useGetQuery(
        {
          subtenantId: patient?.subtenantId,
          isActive: true,
          messageGroup: GDTMessageGroup.DataBlock,
        },
        {
          enabled: claims.permissions.gdt && !!patient?.subtenantId,
        },
      );
    const visitInformationDataBlockType = useMemo(() => {
      return dataBlockTypes.find(
        (dataBlockType) =>
          dataBlockType.variant === DataBlockVariant.Visit &&
          dataBlockType.value === visitInformationDataBlockTypeName,
      );
    }, [dataBlockTypes]);

    const hl7MessageQuery = QueryFactory.HL7MessageQuery.useGetMessagesQuery(
      {
        direction: HL7MessageDirection.Outgoing,
        patientId: patientId,
        pagination_Offset: 0,
        pagination_Limit: 200,
        sorting_SortField: 'dateTime',
        sorting_SortDirection: SortDirection.Desc,
        removeContent: true,
      },
      {
        enabled: claims.permissions.hl7,
        refetchOnMount: false,
      },
    );

    const gdtMessageQuery = QueryFactory.GDTMessageQuery.useGetMessagesQuery(
      {
        direction: GDTMessageDirection.Outgoing,
        patientId: patientId,
        pagination_Offset: 0,
        pagination_Limit: 200,
        sorting_SortField: 'dateTime',
        sorting_SortDirection: SortDirection.Desc,
        removeContent: true,
      },
      {
        enabled: claims.permissions.gdt,
      },
    );
    const caseItems = useMemo(() => {
      function getShowStatistic(typeId: number) {
        const type = dataBlockTypes.find((x) => x.id == typeId);
        return type?.showStatistic;
      }
      function getIsNotDeletable(typeId: number) {
        const type = dataBlockTypes.find((x) => x.id == typeId);
        return type?.variant === DataBlockVariant.Case &&
          type?.value === caseInformationDataBlockTypeName
          ? true
          : false;
      }
      function getTransportInfo(
        dataBlockId: number,
      ): TransportInfo | undefined {
        let transportInfo: TransportInfo = {
          isGDT: false,
          isHL7: false,
        };
        if (
          claims.permissions.hl7 &&
          (hl7OutgoingSettings?.enabled ||
            hl7OutgoingSettings?.outgoingTransporterEnabled) &&
          hl7MessageQuery.data?.totalCount
        ) {
          const hl7Message = hl7MessageQuery.data.data.find(
            (x) =>
              x.outgoingObjectId === dataBlockId && x.outgoingFileId == null,
          );
          if (hl7Message) {
            transportInfo = {
              ...transportInfo,
              isHL7: true,
              hl7Status: getTransportStatus(hl7Message),
              hl7Date: hl7Message.dateTime,
            };
          }
        }
        if (
          claims.permissions.gdt &&
          gdtSettings?.enabled &&
          gdtMessageQuery.data?.totalCount
        ) {
          const gdtMessage = gdtMessageQuery.data.data.find(
            (x) =>
              x.outgoingObjectId === dataBlockId && x.outgoingFileId == null,
          );
          if (gdtMessage) {
            transportInfo = {
              ...transportInfo,
              isGDT: true,
              gdtStatus: getTransportStatus(gdtMessage),
              gdtDate: gdtMessage.dateTime,
            };
          }
        }
        if (transportInfo.isGDT || transportInfo.isHL7) {
          return transportInfo;
        }
        return undefined;
      }
      function getIsHL7Enabled(typeId: number): boolean {
        if (
          !claims.permissions.hl7 ||
          (!hl7OutgoingSettings?.enabled &&
            !hl7OutgoingSettings?.outgoingTransporterEnabled)
        ) {
          return false;
        }
        const templateRule = (hl7TemplateRules ?? []).some(
          (x) => x.dataBlockTypeId === typeId,
        );
        return !!templateRule;
      }
      function getVariant(typeId: number) {
        return dataBlockTypes.find((type) => type.id === typeId)?.variant;
      }

      function getIsGdtEnabled(typeId: number): boolean {
        if (!gdtSettings?.enabled) {
          return false;
        }
        const templateRule = (gdtTemplateRules ?? []).some(
          (x) => x.dataBlockTypeId === typeId,
        );
        return !!templateRule;
      }

      const caseResponse = caseQuery.data;
      if (!caseResponse) {
        return [];
      }
      const caseDataItem: CaseItem = {
        id: `case-data_${caseId}`,
        date: caseResponse.startDate,
        type: CaseItemType.caseData,
        item: caseResponse,
        subitems: (dataBlocksQuery.data?.data ?? [])
          .filter(
            (dataBlock) =>
              getVariant(dataBlock.typeId) === DataBlockVariant.Case,
          )
          // blocks should be ordered by adding time (the one added the first should always be the first in a list)
          .sort((x, y) => (x.id > y.id ? 1 : -1))
          .map((caseBlock) => {
            return {
              key: `c_${caseId}_b_${caseBlock.id}`,
              id: `case-data_${caseId}_block_${caseBlock.id}`,
              date: new Date(),
              type: SubCaseItemType.caseInfo,
              item: caseBlock,
              isNotDeletable: getIsNotDeletable(caseBlock.typeId),
              isStatisticCompleted: caseBlock.isStatisticCompleted,
              showStatistic: getShowStatistic(caseBlock.typeId),
              isGDTEnabled: getIsGdtEnabled(caseBlock.typeId),
              isHL7Enabled: getIsHL7Enabled(caseBlock.typeId),
              transportInfo: getTransportInfo(caseBlock.id),
            };
          }),
      };

      const measurementDataBlocks = (dataBlocksQuery.data?.data ?? []).filter(
        (dataBlock) =>
          getVariant(dataBlock.typeId) === DataBlockVariant.Measurement ||
          getVariant(dataBlock.typeId) === DataBlockVariant.DeviceMeasurement,
      );

      const measurementItems: CaseItem[] = measurementDataBlocks
        .filter((measurement) => !measurement.visitId)
        .map((measurement) => {
          return {
            id: `measurement_${measurement.id}`,
            date: measurement.begin!,
            type: CaseItemType.measurement,
            item: measurement,
            subitems: [],
            isGDTEnabled: getIsGdtEnabled(measurement.typeId),
            isHL7Enabled: getIsHL7Enabled(measurement.typeId),
            transportInfo: getTransportInfo(measurement.id),
            isStatisticCompleted: measurement.isStatisticCompleted,
            showStatistic: getShowStatistic(measurement.typeId),
          };
        });

      const visitItems: CaseItem[] = (visitsQuery.data ?? []).map(
        (visit): CaseItem => {
          const visitMeasurementItems = measurementDataBlocks
            .filter((measurement) => measurement.visitId === visit.id)
            .map((measurement) => {
              return {
                key: `v_${visit.id}_m_${measurement.id}`,
                id: `visit_${visit.id}_measurement_${measurement.id}`,
                date: measurement.begin!,
                type: SubCaseItemType.measurement,
                item: measurement,
                isGDTEnabled: getIsGdtEnabled(measurement.typeId),
                isHL7Enabled: getIsHL7Enabled(measurement.typeId),
                transportInfo: getTransportInfo(measurement.id),
                isStatisticCompleted: measurement.isStatisticCompleted,
                showStatistic: getShowStatistic(measurement.typeId),
              };
            });
          const visitDataItems = (dataBlocksQuery.data?.data ?? [])
            .filter(
              (dataBlock) =>
                getVariant(dataBlock.typeId) === DataBlockVariant.Visit &&
                dataBlock.visitId === visit.id,
            )
            // blocks should be ordered by adding time (the one added the first should always be the first in a list)
            .map((visitBlock) => {
              return {
                key: `v_${visitBlock.visitId}_b_${visitBlock.id}`,
                id: `visit_${visitBlock.visitId}_block_${visitBlock.id}`,
                date: visitBlock.begin ?? new Date(),
                type:
                  visitInformationDataBlockType?.id === visitBlock.typeId
                    ? SubCaseItemType.visitPreview
                    : SubCaseItemType.visitInfo,
                item: visitBlock,
                isStatisticCompleted: visitBlock.isStatisticCompleted,
                showStatistic: getShowStatistic(visitBlock.typeId),
                isGDTEnabled: getIsGdtEnabled(visitBlock.typeId),
                isHL7Enabled: getIsHL7Enabled(visitBlock.typeId),
                transportInfo: getTransportInfo(visitBlock.id),
              };
            });
          const visitPreview = visitDataItems.filter(
            (x) => x.type === SubCaseItemType.visitPreview,
          );
          const visitInfoItems = visitDataItems.filter(
            (x) => x.type === SubCaseItemType.visitInfo,
          );
          (visit as any).customFields = {};
          (visit as any).resources = [];
          return {
            id: `visit_${visit.id}`,
            date: visit.start,
            type: CaseItemType.visit,
            item: visit as VisitListDto & {
              values: { [key: string]: any };
              resources: string[];
            },
            subitems: visitPreview.concat(
              visitInfoItems
                .concat(visitMeasurementItems)
                .sort((a, b) => b.date!.getTime() - a.date!.getTime()),
            ),
          };
        },
      );

      const caseItems: CaseItem[] = visitItems
        .concat(measurementItems)
        .sort((a, b) => b.date.getTime() - a.date.getTime());
      caseItems.unshift(caseDataItem);
      return caseItems;
    }, [
      casesQuery.data,
      caseQuery.data,
      visitsQuery.data,
      dataBlocksQuery.data,
      hl7MessageQuery.data,
      gdtMessageQuery.data,
      gdtTemplateRules,
      hl7TemplateRules,
    ]);

    const isObserversActive = useRef(false);
    useEffect(
      function setupIntersectionObserverToHighlightActiveCaseItem() {
        const initialUrl = location.pathname;

        // stores information if the block is visible currently. Index is position of the block (from the top)
        const itemVisibilities: { id: string; visible: boolean }[] = [];
        // stores the position of a block. Key is block key, value is position of a block
        const itemIndexes: Record<string, number> = {};
        let index = 0;

        function addItemToIndexesAndVisibilities(id: string) {
          itemIndexes[id] = index;
          itemVisibilities[index] = { id: id, visible: false };
          index++;
        }

        caseItems.forEach((x) => {
          addItemToIndexesAndVisibilities(x.id);
          if (x.subitems) {
            x.subitems.forEach((subItem) =>
              addItemToIndexesAndVisibilities(subItem.id),
            );
          }
        });

        const observer = new IntersectionObserver((entries) => {
          if (getCurrentUrl() !== initialUrl) return;

          const currentlyOpenedTabId = getTabId(location);
          if (currentlyOpenedTabId !== tabs.tabId) {
            isObserversActive.current = false;
            observer.disconnect();
            return;
          }

          entries.forEach((entry) => {
            const id = entry.target.getAttribute('data-case-item-id');
            if (!id) {
              return;
            }
            const isVisible = entry.intersectionRatio > 0;

            const item = itemVisibilities[itemIndexes[id]];
            if (!item) {
              console.error(
                'item is undefined!',
                caseId,
                itemVisibilities,
                itemIndexes,
                id,
              );
              return;
            }
            item.visible = isVisible;

            let firstIndex = itemVisibilities.findIndex((x) => x.visible);
            if (firstIndex < 0) {
              // It fixes #23953: Fix scroll behavior.
              // When we scroll as down as possible, then switch to other Case, scroll down, and switch back.
              // Previously there were issues that nothing was selected in case tree.
              firstIndex = itemVisibilities.length - 1;
            }

            const firstVisibleElementId = itemVisibilities[firstIndex].id;
            const highlightedData = itemVisibilities
              .map((x) => x.id)
              .slice(0, firstIndex);

            const parentId = getItemPrefix(firstVisibleElementId);

            const highlightedHeaders = highlightedData
              .filter((x) => !x.startsWith(parentId))
              .map((x) => getItemPrefix(x) + '_header')
              .filter((v, i, a) => a.indexOf(v) === i);

            document!.querySelectorAll(`[data-case-item-link]`).forEach((x) => {
              x.removeAttribute('data-selected');
              x.removeAttribute('data-highlighted-inner-line');
              x.removeAttribute('data-highlighted-outside-line');
            });

            // add data-selected attribute to a first selected node
            const selectedContentsTreeItem = document!.querySelector(
              `[data-case-item-link="${firstVisibleElementId}"]`,
            );

            highlightedData.forEach((id) =>
              document!
                .querySelector(`[data-case-item-link="${id}"]`)
                ?.setAttribute(
                  id.startsWith(parentId)
                    ? 'data-highlighted-inner-line'
                    : 'data-highlighted-outside-line',
                  'true',
                ),
            );

            highlightedHeaders.forEach((id) =>
              document!
                .querySelector(`[data-case-item-link="${id}"]`)
                ?.setAttribute('data-highlighted-inner-line', 'true'),
            );
            document!
              .querySelector(`[data-case-item-link="${parentId}_header"]`)
              ?.setAttribute('data-selected', 'true');

            selectedContentsTreeItem?.setAttribute('data-selected', 'true');
            selectedContentsTreeItem?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            navigate(
              { hash: `caseItem${firstVisibleElementId}` },
              { replace: true },
            );
          });
        });

        // Track all sections that have an `id` applied
        document
          .querySelector(`[data-case-id='${caseId}']`)
          ?.querySelectorAll(`[data-case-item-id]`)
          ?.forEach((section) => {
            observer.observe(section);
          });
        isObserversActive.current = true;

        return () => {
          observer.disconnect();
        };
      },
      [caseItems],
    );

    useEffect(
      function focusCaseItemBasedOnURL() {
        if (
          searchedItem &&
          caseItems.find(
            (item) =>
              item.id === searchedItem ||
              item.subitems?.map((s) => s.id).includes(searchedItem),
          )
        ) {
          if (tabId) {
            // clear tab.search so when we switch tabs this won't fire again
            dispatch(
              NavigationActions.queryStringChanged({ id: tabId, search: '' }),
            );
          }
          changeCurrentItem(searchedItem);
          setSearchedItem(null);
        }
      },
      [caseItems, searchedItem, tabId],
    );
    return (
      <div
        className={styles.caseData}
        data-test-id={'case-container'}
        data-case-id={caseId}
      >
        <CaseContents
          patientId={patientId}
          caseId={caseId}
          caseItems={caseItems}
          changeCurrentItem={changeCurrentItem}
          removeCase={removeCase}
          reopenCase={reopenCase}
          isReadOnly={caseQuery.data?.isClosed ?? false}
        />
        <CaseBlocks
          patientId={props.patientId}
          caseId={caseId}
          caseItems={caseItems}
          changeCurrentItem={changeCurrentItem}
          isReadOnly={caseQuery.data?.isClosed ?? false}
        />
      </div>
    );
  },
);
